import type { HTMLAttributes } from "react";

import { brandVoice } from "../../../../packages/tokens/src";

type ArcBrandSize = "compact" | "regular" | "large" | "hero";
type ArcBrandTone = "standard" | "muted" | "subtle";

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

interface ArcWordmarkProps extends HTMLAttributes<HTMLSpanElement> {
  size?: ArcBrandSize;
  tone?: ArcBrandTone;
}

export function ArcWordmark({
  className,
  size = "regular",
  tone = "standard",
  ...props
}: ArcWordmarkProps) {
  return (
    <span
      {...props}
      className={cx(
        "arc-brand-wordmark",
        `arc-brand-wordmark-${size}`,
        `arc-brand-tone-${tone}`,
        className,
      )}
      aria-label="arc"
    >
      <span className="arc-brand-mark" aria-hidden="true">//</span>
      <span className="arc-brand-word">arc</span>
    </span>
  );
}

interface ArcBrandLineProps extends HTMLAttributes<HTMLSpanElement> {
  size?: ArcBrandSize;
  tone?: ArcBrandTone;
  text?: string;
}

export function ArcBrandSubtag({
  className,
  size = "regular",
  tone = "standard",
  text = brandVoice.logoSubtag,
  ...props
}: ArcBrandLineProps) {
  return (
    <span
      {...props}
      className={cx(
        "arc-brand-subtag",
        `arc-brand-subtag-${size}`,
        `arc-brand-tone-${tone}`,
        className,
      )}
    >
      {text}
    </span>
  );
}

export function ArcRangePromise({
  className,
  size = "regular",
  tone = "standard",
  text = brandVoice.rangePromise,
  ...props
}: ArcBrandLineProps) {
  return (
    <span
      {...props}
      className={cx(
        "arc-brand-promise",
        `arc-brand-promise-${size}`,
        `arc-brand-tone-${tone}`,
        className,
      )}
    >
      {text}
    </span>
  );
}

interface ArcBrandLockupProps extends HTMLAttributes<HTMLDivElement> {
  size?: ArcBrandSize;
  tone?: ArcBrandTone;
  showSubtag?: boolean;
  showPromise?: boolean;
  subtag?: string;
  promise?: string;
}

export function ArcBrandLockup({
  className,
  size = "regular",
  tone = "standard",
  showSubtag = true,
  showPromise = true,
  subtag = brandVoice.logoSubtag,
  promise = brandVoice.rangePromise,
  "aria-label": ariaLabel,
  ...props
}: ArcBrandLockupProps) {
  const label = ariaLabel ?? ["arc", showSubtag ? subtag : null, showPromise ? promise : null]
    .filter(Boolean)
    .join(". ");

  return (
    <div
      {...props}
      className={cx(
        "arc-brand-lockup",
        `arc-brand-lockup-${size}`,
        `arc-brand-tone-${tone}`,
        className,
      )}
      aria-label={label}
    >
      <ArcWordmark size={size} tone={tone} />
      {showSubtag ? <ArcBrandSubtag size={size} tone={tone} text={subtag} /> : null}
      {showPromise ? <ArcRangePromise size={size} tone={tone} text={promise} /> : null}
    </div>
  );
}
